import { useState, useMemo } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, hotMovies, latestMovies, tvSeries } from '@/mocks/movies';
import type { MovieItem } from '@/mocks/movies';

const allMovies: MovieItem[] = [...hotMovies, ...latestMovies, ...tvSeries];

function getCategoryName(slug: string): string {
  const cat = categories.find((c) => c.slug === slug);
  return cat ? cat.name : '全部';
}

export default function CategoryPage() {
  const { slug = 'all' } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const [sortBy, setSortBy] = useState<'rating' | 'year' | 'views'>('rating');

  const filteredMovies = useMemo(() => {
    let result = allMovies;

    if (slug !== 'all') {
      result = result.filter((m) => m.category === slug);
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (m) =>
          m.title.toLowerCase().includes(q) ||
          m.tags.some((t) => t.toLowerCase().includes(q)) ||
          m.actors.some((a) => a.toLowerCase().includes(q))
      );
    }

    switch (sortBy) {
      case 'rating':
        result = [...result].sort((a, b) => b.rating - a.rating);
        break;
      case 'year':
        result = [...result].sort((a, b) => b.year - a.year);
        break;
      case 'views':
        result = [...result].sort((a, b) => {
          const parseViews = (v: string) => parseFloat(v.replace('亿', '')) * 10000;
          return parseViews(b.views) - parseViews(a.views);
        });
        break;
    }

    return result;
  }, [slug, searchQuery, sortBy]);

  const categoryName = getCategoryName(slug);

  return (
    <>
      <ScrollToTop />
      <Navbar alwaysShowBg />
      <main className="pt-16">
        <div className="w-full px-4 md:px-8 py-4 md:py-6 bg-background-100 border-b border-background-300/20">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: categoryName },
              ]}
            />

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-2">
              <div>
                <h1 className="text-xl md:text-2xl font-heading text-foreground-100">
                  {searchQuery ? `搜索: "${searchQuery}"` : categoryName}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  共找到 <strong className="text-primary-400">{filteredMovies.length}</strong> 部影片
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500">排序:</span>
                {[
                  { value: 'rating' as const, label: '评分' },
                  { value: 'year' as const, label: '年份' },
                  { value: 'views' as const, label: '热度' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setSortBy(opt.value)}
                    className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors ${
                      sortBy === opt.value
                        ? 'bg-primary-500 text-background-950 font-medium'
                        : 'bg-background-200/50 text-foreground-500 hover:text-foreground-300'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-8 py-2 md:py-3 bg-background-50 border-b border-background-300/10 overflow-x-auto">
          <div className="max-w-7xl mx-auto flex items-center gap-1.5 md:gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-3 md:px-4 py-1.5 rounded-full text-xs md:text-sm whitespace-nowrap transition-colors ${
                  slug === cat.slug
                    ? 'bg-primary-500 text-background-950 font-medium'
                    : 'bg-background-100 text-foreground-500 hover:text-foreground-300 hover:bg-background-200/50'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        <div className="w-full px-4 md:px-8 py-6 md:py-8">
          <div className="max-w-7xl mx-auto">
            {filteredMovies.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-search-line text-2xl text-foreground-500"></i>
                </div>
                <p className="text-foreground-500">未找到相关影片</p>
                <Link
                  to="/category/all"
                  className="inline-block mt-4 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-950 rounded-lg text-sm whitespace-nowrap transition-colors"
                >
                  浏览全部影片
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-5">
                {filteredMovies.map((movie) => (
                  <Link
                    key={movie.id}
                    to={`/article/${movie.id}`}
                    className="group block"
                  >
                    <div className="relative overflow-hidden rounded-lg aspect-[2/3] bg-background-200 mb-3">
                      <img
                        src={movie.imageUrl}
                        alt={movie.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                        loading="lazy"
                      />
                      <div className="absolute top-2 right-2 px-1.5 py-0.5 bg-primary-500/90 text-background-950 rounded text-xs font-medium">
                        {movie.quality}
                      </div>
                      <div className="absolute top-2 left-2 px-1.5 py-0.5 bg-background-950/70 backdrop-blur-sm text-primary-400 rounded text-xs font-medium flex items-center gap-0.5">
                        <span className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-star-fill text-amber-400"></i>
                        </span>
                        {movie.rating}
                      </div>
                    </div>
                    <h3 className="text-sm md:text-base font-medium text-foreground-200 group-hover:text-primary-400 transition-colors line-clamp-1 mb-1">
                      {movie.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-1 mb-1">
                      {movie.subtitle}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-foreground-600">
                      <span>{movie.year}</span>
                      <span className="w-0.5 h-0.5 rounded-full bg-foreground-600"></span>
                      <span>{movie.duration}</span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="w-full px-4 md:px-8 pb-10">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-6 rounded-lg bg-background-100 border border-background-300/20">
              <div>
                <h3 className="text-sm font-medium text-foreground-200 mb-1">没找到想看的？</h3>
                <p className="text-xs text-foreground-500">奇优影院片库每日更新，更多精彩内容持续上架</p>
              </div>
              <Link
                to="/category/all"
                className="flex items-center gap-1.5 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-background-950 rounded-lg text-sm font-medium whitespace-nowrap transition-colors"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-refresh-line text-sm"></i>
                </span>
                查看全部
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}